import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ExampleBadge } from '@/components/ui/ExampleBadge'
import { AreaState, NoAccess } from '../../admin/shared/ui'
import { erpSettingsQuery, useAdminMe, useSaveErpSettings } from '../api'
import type { ErpSettings, ErpSettingsPatch } from '../contract'
import { StateHeader, type PulseItem } from '../ui/calm'

/* ERP · Configurações — peças das telas: cabeçalho calmo (StateHeader), guarda de admin, grupo de linhas, interruptor
   acessível, número com limite, linha de configuração e o carregamento das configurações. */

export const CONFIG_GROUP = 'Configurações'
export const fieldInput = 'min-h-9 rounded-[10px] border border-line-2 bg-panel px-2.5 text-[14px] font-normal text-fg'

/** Só admin (Tecnologia) abre as Configurações do ERP; os outros veem o aviso. */
export function adminOnly(C: ComponentType): ComponentType {
    function Guarded() {
        const me = useAdminMe()
        if (!me) return <div role="status" className="text-[14px] text-muted">Carregando…</div>
        if (me.role !== 'admin') return <NoAccess title="Só a Tecnologia abre as Configurações" text="Menus, regras e dados da organização ficam com o administrador do sistema. Peça a ele." />
        return <C />
    }
    Guarded.displayName = `AdminOnly(${C.displayName ?? C.name})`
    return Guarded
}

/** Cabeçalho calmo do que a seção mostra: título (vira o "Configurações · <seção>" de cima) e, com as configurações
    carregadas, `head` diz o estado numa frase, no máximo uma linha apagada e até 3 números. */
export type SettingsHead = { title: ReactNode; sub?: ReactNode; pulse?: PulseItem[] }

/** Cabeçalho + configurações carregadas (com estados de carregando/erro do Panel). */
export function SettingsScreen({ title, head, example, actions, children }: {
    title: string
    head?: (settings: ErpSettings) => SettingsHead
    example?: boolean
    actions?: ReactNode
    children: (settings: ErpSettings, save: (patch: ErpSettingsPatch) => void, saving: boolean) => ReactNode
}) {
    const query = useQuery(erpSettingsQuery)
    const saver = useSaveErpSettings()
    const h = query.data && head ? head(query.data) : { title }
    return (
        <>
            <StateHeader eyebrow={<ConfigEyebrow section={title} example={example} />} title={h.title} sub={h.sub} pulse={h.pulse} actions={actions} />
            <AreaState query={query} noAccess={{ title: 'Você não vê as configurações', text: 'Peça a um administrador.' }}>
                {s => children(s, patch => saver.mutate(patch), saver.isPending)}
            </AreaState>
        </>
    )
}

/** "Configurações · <seção>" (+ "dados de exemplo" quando o grafo é o de exemplo). */
export function ConfigEyebrow({ section, example }: { section: string; example?: boolean }) {
    return <>{CONFIG_GROUP} · {section} <ExampleBadge show={!!example} /></>
}

/** Grupo de linhas: título pequeno e um painel branco (no lugar dos cartões com KPI). */
export function SettingsGroup({ id, title, aside, children, className }: { id?: string; title: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
    const headingId = id ? `${id}-title` : undefined
    return (
        <section aria-labelledby={headingId} className={cn('flex min-w-0 flex-col gap-1.5', className)}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-0.5">
                <h2 id={headingId} className="m-0 text-[13.5px] font-semibold text-fg">{title}</h2>
                {aside && <span className="text-[12px] text-muted">{aside}</span>}
            </div>
            <div className="flex min-w-0 flex-col gap-2 rounded-[14px] border border-line bg-panel px-3.5 py-2.5">{children}</div>
        </section>
    )
}

/** Interruptor (role="switch") com o visual do Panel. */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (on: boolean) => void; label: string; disabled?: boolean }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            className={cn(
                'relative inline-flex h-6 w-10 flex-none cursor-pointer items-center rounded-full border-0 p-0 transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--green)] disabled:cursor-not-allowed disabled:opacity-50',
            )}
            style={{ background: checked ? 'var(--green)' : 'var(--line-2)' }}
        >
            <span aria-hidden="true" className="absolute top-[3px] size-[18px] rounded-full bg-white transition-[left]" style={{ left: checked ? 19 : 3, boxShadow: '0 1px 2px var(--shadow)' }} />
        </button>
    )
}

/** Número com limite: grava ao sair do campo (ou Enter) quando o valor é válido e diferente. */
export function NumberField({ value, min, max, label, unit, onCommit, disabled }: {
    value: number; min: number; max: number; label: string; unit?: string; onCommit: (v: number) => void; disabled?: boolean
}) {
    const [text, setText] = useState(String(value))
    useEffect(() => setText(String(value)), [value])
    const n = Number(text)
    const invalid = text.trim() === '' || !Number.isInteger(n) || n < min || n > max
    const commit = () => {
        if (invalid) return setText(String(value))
        if (n !== value) onCommit(n)
    }
    return (
        <span className="inline-flex flex-wrap items-center gap-2 text-[13px] text-muted">
            <input
                type="number"
                inputMode="numeric"
                min={min}
                max={max}
                step={1}
                value={text}
                disabled={disabled}
                aria-label={label}
                aria-invalid={invalid || undefined}
                title={`De ${min} a ${max}`}
                onChange={e => setText(e.target.value)}
                onBlur={commit}
                onKeyDown={e => { if (e.key === 'Enter') commit() }}
                className={cn(fieldInput, 'tabular w-[84px] text-right', invalid && 'border-[var(--red)]')}
            />
            {unit}
        </span>
    )
}

/** Linha "título + explicação | controle". */
export function SettingRow({ title, hint, children }: { title: ReactNode; hint?: ReactNode; children: ReactNode }) {
    return (
        <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-t border-line py-2 first:border-t-0">
            <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-0.5">
                <span className="text-[13.5px] font-medium">{title}</span>
                {hint && <span className="text-[12.5px] text-muted">{hint}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">{children}</div>
        </div>
    )
}

/** Regra que não se desliga (cartão bloqueado). */
export function LockedRule({ children }: { children: ReactNode }) {
    return (
        <div className="flex items-start gap-2.5 rounded-[12px] bg-[var(--raised)] px-3 py-2.5 text-[13px] leading-[1.45]">
            <Lock size={15} aria-hidden="true" className="mt-0.5 flex-none text-muted" />
            <span>{children}</span>
        </div>
    )
}

export function Note({ children }: { children: ReactNode }) {
    return <p className="m-0 rounded-[10px] bg-[var(--raised)] px-3 py-2 text-[12.5px] text-muted">{children}</p>
}

export const fmtDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
